import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, Search } from 'lucide-react';
import { licenseService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { apiPath } from '../../utils/apiBaseUrl';

const PublicIssuedLicenses = () => {
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [result, setResult] = useState({ items: [], total: 0, page: 1, pages: 1, page_size: 20 });

  const load = useCallback(async (nextPage, query) => {
    setStatus('loading');
    setError('');
    try {
      const { data } = await licenseService.consultarLicencasPublicas({
        q: query,
        page: nextPage,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setResult({
        items,
        total: Number(data?.total) || 0,
        page: Number(data?.page) || nextPage,
        pages: Number(data?.pages) || 1,
        page_size: Number(data?.page_size) || 20,
      });
      setPage(Number(data?.page) || nextPage);
      setStatus(items.length ? 'results' : 'empty');
    } catch (err) {
      setStatus('error');
      setResult({ items: [], total: 0, page: nextPage, pages: 1, page_size: 20 });
      setError(userFacingError(err, 'loadError'));
    }
  }, []);

  useEffect(() => {
    load(1, '');
  }, [load]);

  const buscar = (event) => {
    event.preventDefault();
    const needle = q.trim();
    setApplied(needle);
    setPage(1);
    load(1, needle);
  };

  const irPara = (next) => {
    setPage(next);
    load(next, applied);
  };

  const from = result.total === 0 ? 0 : (result.page - 1) * result.page_size + 1;
  const to = Math.min(result.page * result.page_size, result.total);

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
      <h2 className="text-base font-semibold text-slate-900">Licenças emitidas</h2>
      <p className="mt-1 text-sm text-slate-500">
        Lista das licenças já emitidas por este órgão. A busca olha número, tipo, requerente e empreendimento.
      </p>
      <form className="mt-4 flex flex-col gap-2 sm:flex-row" onSubmit={buscar}>
        <label className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
            <Search className="h-4 w-4" aria-hidden />
          </span>
          <input
            className="form-input pl-10"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Número, tipo, requerente ou empreendimento"
            aria-label="Buscar licença emitida"
          />
        </label>
        <button type="submit" className="btn btn-primary sm:min-w-[140px]" disabled={status === 'loading'}>
          {status === 'loading' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Buscando…
            </>
          ) : (
            'Buscar'
          )}
        </button>
      </form>

      <div className="mt-5" aria-live="polite">
        {status === 'loading' && result.items.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Carregando licenças emitidas…
          </p>
        ) : null}
        {status === 'error' ? <p className="text-sm text-red-700">{error}</p> : null}
        {status === 'empty' ? (
          <p className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-600">
            {applied
              ? `Nenhuma licença emitida encontrada para «${applied}».`
              : 'Este órgão ainda não tem licença emitida para consulta.'}
          </p>
        ) : null}
        {result.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-semibold">Número</th>
                  <th className="px-3 py-2 font-semibold">Tipo</th>
                  <th className="px-3 py-2 font-semibold">Requerente</th>
                  <th className="px-3 py-2 font-semibold">Empreendimento</th>
                  <th className="px-3 py-2 font-semibold">PDF</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((row, index) => (
                  <tr key={`${row.numero}-${index}`} className="border-b border-slate-100">
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">{row.numero}</td>
                    <td className="px-3 py-2 text-slate-700">{row.tipo}</td>
                    <td className="px-3 py-2 text-slate-700">{row.requerente}</td>
                    <td className="px-3 py-2 text-slate-700">{row.empreendimento}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {row.pdf_url ? (
                        <a
                          className="inline-flex items-center gap-1 font-medium text-emerald-800 hover:underline"
                          href={apiPath(row.pdf_url)}
                          download
                        >
                          <Download className="h-4 w-4" aria-hidden />
                          Baixar
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {result.total > 0 ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
          <p>
            {from}–{to} de {result.total}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={status === 'loading' || result.page <= 1}
              onClick={() => irPara(result.page - 1)}
            >
              Anterior
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={status === 'loading' || result.page >= result.pages}
              onClick={() => irPara(result.page + 1)}
            >
              Próxima
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default PublicIssuedLicenses;
